import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Alert, Button, Spin } from 'antd';
import { useHistory, useLocation } from 'react-router-dom';
import { useIntl } from 'react-intl';

import AttendanceEventService from '@src/services/AttendanceEventService';
import Card from '@src/containers/App/Auth/components/Card';

import styles from './styles.module.less';

const REQUEST_STATUS = {
  CONFIRM: 'confirm',
  LOADING: 'loading',
  SUCCESS: 'success',
  ERROR: 'error',
  EXPIRED: 'expired',
};

const ATTENDANCE_TYPE_SHIFT_REPLACEMENT = 'SHIFT_REPLACEMENT';

interface CancelTokenPayload {
  exp?: number;
  attendanceType?: string;
}

const CancelAttendanceEvent: React.FC = () => {
  const intl = useIntl();
  const history = useHistory();
  const location = useLocation();
  const [status, setStatus] = useState(REQUEST_STATUS.LOADING);
  const [errorMessage, setErrorMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [rejectUntilLabel, setRejectUntilLabel] = useState('');
  const [isReplacement, setIsReplacement] = useState(false);
  const hasRequestedRef = useRef(false);
  const inFlightRef = useRef(false);

  const token = useMemo(
    () => new URLSearchParams(location.search).get('token')?.trim() || '',
    [location.search],
  );

  const attendanceEventService = useMemo(() => new AttendanceEventService(), []);

  const messageId = useCallback(
    (baseKey: string) =>
      isReplacement ? `attendanceEvent.cancel.replacement.${baseKey}` : `attendanceEvent.cancel.${baseKey}`,
    [isReplacement],
  );

  const parseJwtPayload = (jwtToken: string): CancelTokenPayload | null => {
    try {
      const [, payloadBase64] = jwtToken.split('.');
      if (!payloadBase64) return null;
      const base64 = payloadBase64.replace(/-/g, '+').replace(/_/g, '/');
      const json = decodeURIComponent(
        atob(base64)
          .split('')
          .map(char => `%${`00${char.charCodeAt(0).toString(16)}`.slice(-2)}`)
          .join(''),
      );
      return JSON.parse(json);
    } catch (error) {
      return null;
    }
  };

  const toDeadlineLabel = (deadlineMillis: number) =>
    intl.formatDate(deadlineMillis, {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });

  const setDeadlineByTokenPayload = (payload: CancelTokenPayload | null): void => {
    if (payload?.exp && Number.isFinite(payload.exp)) {
      setRejectUntilLabel(toDeadlineLabel(payload.exp * 1000));
    }
  };

  const onSubmit = useCallback(async () => {
    if (inFlightRef.current) return;

    if (!token) {
      setStatus(REQUEST_STATUS.EXPIRED);
      return;
    }

    inFlightRef.current = true;
    setIsSubmitting(true);
    setErrorMessage('');
    setStatus(REQUEST_STATUS.LOADING);
    try {
      const response = await attendanceEventService.cancelAttendanceEventByToken(token);
      const body = response?.data || {};

      if (response?.ok) {
        setStatus(REQUEST_STATUS.SUCCESS);
        return;
      }

      const apiMessage =
        typeof body?.error === 'string' && body.error
          ? body.error
          : intl.formatMessage({ id: messageId('genericError') });

      if (response?.status === 401) {
        setStatus(REQUEST_STATUS.EXPIRED);
        setErrorMessage(apiMessage);
        return;
      }

      setStatus(REQUEST_STATUS.ERROR);
      setErrorMessage(apiMessage);
    } catch (error) {
      setStatus(REQUEST_STATUS.ERROR);
      setErrorMessage(intl.formatMessage({ id: messageId('networkError') }));
    } finally {
      inFlightRef.current = false;
      setIsSubmitting(false);
    }
  }, [attendanceEventService, intl, messageId, token]);

  useEffect(() => {
    if (hasRequestedRef.current) {
      return;
    }
    hasRequestedRef.current = true;
    if (!token) {
      setStatus(REQUEST_STATUS.EXPIRED);
      return;
    }

    const payload = parseJwtPayload(token);
    const expirationMillis =
      payload?.exp && Number.isFinite(payload.exp) ? payload.exp * 1000 : null;

    setIsReplacement(payload?.attendanceType === ATTENDANCE_TYPE_SHIFT_REPLACEMENT);

    if (!expirationMillis) {
      setStatus(REQUEST_STATUS.EXPIRED);
      setErrorMessage(
        intl.formatMessage({
          id:
            payload?.attendanceType === ATTENDANCE_TYPE_SHIFT_REPLACEMENT
              ? 'attendanceEvent.cancel.replacement.expiredDescription'
              : 'attendanceEvent.cancel.expiredDescription',
        }),
      );
      return;
    }

    setDeadlineByTokenPayload(payload);
    if (Date.now() > expirationMillis) {
      setStatus(REQUEST_STATUS.EXPIRED);
      return;
    }
    setStatus(REQUEST_STATUS.CONFIRM);
  }, [intl, token]);

  const onGoToSignIn = () => {
    history.push('/signin');
  };

  if (status === REQUEST_STATUS.LOADING || isSubmitting) {
    return (
      <div className={styles.wrapper}>
        <div className={styles.card}>
          <Spin size="large" />
        </div>
      </div>
    );
  }

  if (status === REQUEST_STATUS.CONFIRM) {
    return (
      <Card>
        <Alert
          type="warning"
          showIcon
          className={styles.alert}
          message={intl.formatMessage({ id: messageId('confirmTitle') })}
          description={intl.formatMessage(
            { id: messageId('confirmDescription') },
            { rejectUntil: rejectUntilLabel || '-' },
          )}
        />
        <div className={styles.actions}>
          <Button onClick={onGoToSignIn} className="gx-mb-0">
            {intl.formatMessage({ id: 'attendanceEvent.cancel.goToApp' })}
          </Button>
          <Button
            type="primary"
            danger
            onClick={onSubmit}
            loading={isSubmitting}
            disabled={isSubmitting}
            className="gx-mb-0"
          >
            {intl.formatMessage({ id: messageId('confirmButton') })}
          </Button>
        </div>
      </Card>
    );
  }

  if (status === REQUEST_STATUS.SUCCESS) {
    return (
      <Card>
        <Alert
          type="success"
          showIcon
          className={styles.alert}
          message={intl.formatMessage({ id: messageId('successTitle') })}
          description={intl.formatMessage({ id: messageId('successDescription') })}
        />
        <Button type="primary" onClick={onGoToSignIn} className="gx-mb-0">
          {intl.formatMessage({ id: 'attendanceEvent.cancel.goToApp' })}
        </Button>
      </Card>
    );
  }

  const isExpired = status === REQUEST_STATUS.EXPIRED;
  const titleId = isExpired ? messageId('expiredTitle') : messageId('errorTitle');
  const defaultDescriptionId = isExpired
    ? messageId('expiredDescription')
    : messageId('genericError');
  const defaultDescription = intl.formatMessage(
    { id: defaultDescriptionId },
    { rejectUntil: rejectUntilLabel || '-' },
  );

  return (
    <Card>
      <Alert
        type={isExpired ? 'warning' : 'error'}
        showIcon
        className={styles.alert}
        message={intl.formatMessage({ id: titleId })}
        description={errorMessage || defaultDescription}
      />
      <div className={styles.actions}>
        <Button onClick={onGoToSignIn} className="gx-mb-0">
          {intl.formatMessage({ id: 'attendanceEvent.cancel.goToApp' })}
        </Button>
      </div>
    </Card>
  );
};

export default CancelAttendanceEvent;
